import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useToast } from '@/hooks/useToast'
import i18n, { type TransKey } from '@/i18n'
import { cn } from '@/lib/utils'
import { HostedFieldsData } from '@/pages/client-side/Payment/components/PaymentData'
import { BIT_MIN_ILS } from '@/pages/client-side/Payment/constants'

import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Spinner } from './ui/spinner'

const HF_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thostedf.js'

function tranzilaHebrewEnglish(appLanguage: string | undefined): 'hebrew' | 'english' {
  return appLanguage?.toLowerCase().startsWith('he') ? 'hebrew' : 'english'
}

type HostedFieldKey = 'credit_card_number' | 'cvv' | 'expiry'

const INITIAL_FIELD_VALID: Record<HostedFieldKey, boolean> = {
  credit_card_number: false,
  cvv: false,
  expiry: false,
}

const INITIAL_BLURRED: Record<HostedFieldKey, boolean> = {
  credit_card_number: false,
  cvv: false,
  expiry: false,
}

function isHostedFieldKey(field: string): field is HostedFieldKey {
  return field === 'credit_card_number' || field === 'cvv' || field === 'expiry'
}

interface TranzilaFocusBlurEvent {
  type?: string
  field?: string
}

export type TranzilaCheckoutMode = 'card' | 'bit'

interface Props {
  hostedFieldsData: HostedFieldsData
  /** Mirrors payment method from summary: card fields + pay, or Bit-only pay. */
  checkoutMode: TranzilaCheckoutMode
  onSuccess: (response: TranzilaChargeResponse) => void
  onError: (error: string) => void
}

function hostedFieldShellClass(
  key: HostedFieldKey,
  ctx: {
    focusedField: HostedFieldKey | null
    hoveredField: HostedFieldKey | null
    fieldErrors: Record<string, string>
    fieldValid: Record<HostedFieldKey, boolean>
    blurredFields: Record<HostedFieldKey, boolean>
  }
): string {
  const serverErr = Boolean(ctx.fieldErrors[key])
  const showClientError = !ctx.fieldValid[key] && ctx.blurredFields[key]
  const showErrorChrome = serverErr || showClientError
  const focused = ctx.focusedField === key
  const hovered = ctx.hoveredField === key && !focused

  return cn(
    'tranzila-hf-field flex h-12 w-full items-center rounded-2xl border bg-white px-4 transition-all duration-200',
    showErrorChrome && 'border-destructive shadow-[0_0_0_2px_rgba(255,0,0,0.2)]',
    !showErrorChrome && focused && 'border-icon-primary shadow-[0_0_0_2px_rgba(0,174,255,0.2)]',
    !showErrorChrome && !focused && hovered && 'border-[#B5BAC2]',
    !showErrorChrome && !focused && !hovered && 'border-border-default'
  )
}

export function TranzilaHostedFields({ hostedFieldsData, checkoutMode, onSuccess, onError }: Props) {
  const { t } = useTranslation()
  const { showErrorToast } = useToast()
  const fieldsRef = useRef<TranzilaHostedFieldsInstance | null>(null)
  const onErrorRef = useRef(onError)
  const onSuccessRef = useRef(onSuccess)
  const fieldValidRef = useRef<Record<HostedFieldKey, boolean>>({ ...INITIAL_FIELD_VALID })

  onErrorRef.current = onError
  onSuccessRef.current = onSuccess

  const [isReady, setIsReady] = useState(false)
  const [isCharging, setIsCharging] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [fieldValid, setFieldValid] = useState<Record<HostedFieldKey, boolean>>({ ...INITIAL_FIELD_VALID })
  const [focusedField, setFocusedField] = useState<HostedFieldKey | null>(null)
  const [hoveredField, setHoveredField] = useState<HostedFieldKey | null>(null)
  const [blurredFields, setBlurredFields] = useState<Record<HostedFieldKey, boolean>>({ ...INITIAL_BLURRED })

  const hfInstanceId = useMemo(() => `hf-${crypto.randomUUID().replace(/-/g, '')}`, [])

  const fieldIds = useMemo(
    () => ({
      credit_card_number: `${hfInstanceId}-card-number`,
      cvv: `${hfInstanceId}-cvv`,
      expiry: `${hfInstanceId}-expiry`,
    }),
    [hfInstanceId]
  )

  const selectors = useMemo(
    () => ({
      credit_card_number: `#${fieldIds.credit_card_number}`,
      cvv: `#${fieldIds.cvv}`,
      expiry: `#${fieldIds.expiry}`,
    }),
    [fieldIds]
  )

  const clearHostContainers = useCallback(() => {
    for (const suffix of ['card-number', 'cvv', 'expiry'] as const) {
      document.getElementById(`${hfInstanceId}-${suffix}`)?.replaceChildren()
    }
  }, [hfInstanceId])

  const allFieldsValid = fieldValid.credit_card_number && fieldValid.cvv && fieldValid.expiry

  const shellCtx = useMemo(
    () => ({ focusedField, hoveredField, fieldErrors, fieldValid, blurredFields }),
    [focusedField, hoveredField, fieldErrors, fieldValid, blurredFields]
  )

  const applyChargeResult = useCallback(
    (err: TranzilaChargeError | null, response: TranzilaChargeResponse | null, mapFieldErrors: boolean) => {
      setIsCharging(false)
      if (err) {
        const messages = err.messages || []
        if (messages.length > 0) {
          if (mapFieldErrors) {
            const errorMap: Record<string, string> = {}
            messages.forEach(msg => {
              errorMap[msg.param] = msg.message
            })
            setFieldErrors(errorMap)
          }
          onErrorRef.current(messages.map(m => m.message).join(', '))
        } else {
          onErrorRef.current(i18n.t('payment.serverError'))
        }
        return
      }
      if (response) {
        if (response.transaction_response?.success) {
          onSuccessRef.current(response)
        } else {
          const errorMsg = response.transaction_response?.error || i18n.t('payment.serverError')
          onErrorRef.current(errorMsg)
        }
      }
    },
    []
  )

  const loadScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof TzlaHostedFields !== 'undefined') {
        resolve()
        return
      }
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${HF_SCRIPT_URL}"]`)
      if (existing) {
        if (typeof TzlaHostedFields !== 'undefined') {
          resolve()
          return
        }
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error('Failed to load Tranzila hosted fields script')))
        return
      }
      const script = document.createElement('script')
      script.src = HF_SCRIPT_URL
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Tranzila hosted fields script'))
      document.head.appendChild(script)
    })
  }, [])

  useEffect(() => {
    let cancelled = false

    setIsReady(false)
    setFieldErrors({})
    setFieldValid({ ...INITIAL_FIELD_VALID })
    fieldValidRef.current = { ...INITIAL_FIELD_VALID }
    setBlurredFields({ ...INITIAL_BLURRED })
    setFocusedField(null)
    setHoveredField(null)

    loadScript()
      .then(() => {
        if (cancelled) return

        clearHostContainers()

        const instance = TzlaHostedFields.create({
          sandbox: false,
          fields: {
            credit_card_number: {
              selector: selectors.credit_card_number,
              placeholder: '',
              tabindex: 1,
            },
            expiry: {
              selector: selectors.expiry,
              placeholder: 'MM/YY',
              version: '1',
              tabindex: 2,
            },
            cvv: {
              selector: selectors.cvv,
              placeholder: '123',
              tabindex: 3,
            },
          },
          styles: {
            input: {
              'font-size': '16px',
              'font-family': 'inherit',
              color: '#333333',
              width: '100%',
            },
            select: {
              'font-size': '14px',
              'font-family': 'inherit',
              color: '#333333',
              direction: 'ltr',
              height: 'auto',
              width: '100%',
            },
          },
        })

        fieldsRef.current = instance

        instance.onEvent('ready', () => {
          if (!cancelled) setIsReady(true)
        })

        instance.onEvent('focus', (msg: unknown) => {
          const m = msg as TranzilaFocusBlurEvent & { fieldName?: string }
          const fieldKey = m.field ?? m.fieldName
          if (!fieldKey || !isHostedFieldKey(fieldKey)) return
          if (!cancelled) setFocusedField(fieldKey)
        })

        instance.onEvent('blur', (msg: unknown) => {
          const m = msg as TranzilaFocusBlurEvent & { fieldName?: string }
          const fieldKey = m.field ?? m.fieldName
          if (!fieldKey || !isHostedFieldKey(fieldKey)) return
          if (cancelled) return
          setFocusedField(prev => (prev === fieldKey ? null : prev))
          setBlurredFields(prev => ({ ...prev, [fieldKey]: true }))
        })

        instance.onEvent('validityChange', (event: unknown) => {
          const e = event as { field?: string; isValid?: boolean }
          if (!e.field || !isHostedFieldKey(e.field)) return
          const key = e.field
          const valid = Boolean(e.isValid)
          fieldValidRef.current[key] = valid
          if (!cancelled) {
            setFieldValid(prev => ({ ...prev, [key]: valid }))
            if (valid) {
              setFieldErrors(prev => {
                const next = { ...prev }
                delete next[key]
                return next
              })
            }
          }
        })
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current(i18n.t('payment.serverError'))
      })

    return () => {
      cancelled = true
      fieldsRef.current = null
      clearHostContainers()
    }
  }, [loadScript, hostedFieldsData.thtk, clearHostContainers, selectors])

  const handleCharge = useCallback(() => {
    if (!fieldsRef.current || isCharging) return

    const v = fieldValidRef.current
    if (!v.credit_card_number || !v.cvv || !v.expiry) {
      showErrorToast(t('payment.fillCardFields'))
      return
    }

    setIsCharging(true)

    fieldsRef.current.charge(
      {
        terminal_name: hostedFieldsData.terminalName,
        amount: hostedFieldsData.sum,
        thtk: hostedFieldsData.thtk,
        currency_code: 'ILS',
        tran_mode: 'A',
        tokenize: true,
        notify_url: hostedFieldsData.notifyUrl,
        contact: hostedFieldsData.contact,
        email: hostedFieldsData.email,
        phone: hostedFieldsData.phone,
        pdesc: hostedFieldsData.pdesc,
        response_language: 'hebrew',
      },
      (err, response) => {
        applyChargeResult(err, response, true)
      }
    )
  }, [applyChargeResult, hostedFieldsData, isCharging, showErrorToast, t])

  const handleChargeBit = useCallback(() => {
    if (!fieldsRef.current || isCharging) return

    if (hostedFieldsData.sum < BIT_MIN_ILS) {
      showErrorToast(t('payment.bitMinimumAmount'))
      return
    }

    const lang = tranzilaHebrewEnglish(i18n.language)
    setIsCharging(true)

    const bitParams: TranzilaChargeBitParams = {
      terminal_name: hostedFieldsData.terminalName,
      amount: hostedFieldsData.sum,
      currency_code: 'ILS',
      payment_plan: '8',
      thtk: hostedFieldsData.thtk,
      contact: hostedFieldsData.contact,
      response_language: lang,
      other_installments_amount: hostedFieldsData.sum,
      bit_language: lang,
      transaction_layout: 'qr-sms',
    }

    fieldsRef.current.chargeBit(bitParams, (err, response) => {
      applyChargeResult(err, response, false)
    })
  }, [applyChargeResult, hostedFieldsData, isCharging, showErrorToast, t])

  const bitEligible = hostedFieldsData.sum >= BIT_MIN_ILS
  const showCardCheckout = checkoutMode === 'card'
  const showBitCheckout = checkoutMode === 'bit'

  const fieldHelper = (key: HostedFieldKey) => {
    const server = fieldErrors[key]
    const clientInvalid = !fieldValid[key] && blurredFields[key]
    if (!server && !clientInvalid) return null
    return <p className='mt-1 px-4 text-xs text-[#FF0000]'>{server ?? t('payment.invalidField')}</p>
  }

  return (
    <Card>
      <CardContent className='flex flex-col gap-4'>
        {!isReady && (
          <div className='flex items-center justify-center py-8'>
            <Spinner className='size-8' />
          </div>
        )}

        <div className={isReady ? '' : 'invisible h-0 overflow-hidden'}>
          <div className='flex flex-col gap-4'>
            <div className={showBitCheckout ? 'sr-only' : undefined} aria-hidden={showBitCheckout}>
              <div onMouseEnter={() => setHoveredField('credit_card_number')} onMouseLeave={() => setHoveredField(null)}>
                <label className='mb-1 block text-sm text-[#4B4B4B]' htmlFor={fieldIds.credit_card_number}>
                  {t('payment.cardNumber')}
                </label>
                <div id={fieldIds.credit_card_number} className={hostedFieldShellClass('credit_card_number', shellCtx)} />
                {fieldHelper('credit_card_number')}
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <div onMouseEnter={() => setHoveredField('expiry')} onMouseLeave={() => setHoveredField(null)}>
                  <label className='mb-1 block text-sm text-[#4B4B4B]' htmlFor={fieldIds.expiry}>
                    {t('payment.expiryDate')}
                  </label>
                  <div id={fieldIds.expiry} className={hostedFieldShellClass('expiry', shellCtx)} />
                  {fieldHelper('expiry')}
                </div>
                <div onMouseEnter={() => setHoveredField('cvv')} onMouseLeave={() => setHoveredField(null)}>
                  <label className='mb-1 block text-sm text-[#4B4B4B]' htmlFor={fieldIds.cvv}>
                    {t('payment.cvv' as TransKey)}
                  </label>
                  <div id={fieldIds.cvv} className={hostedFieldShellClass('cvv', shellCtx)} />
                  {fieldHelper('cvv')}
                </div>
              </div>
            </div>

            {showCardCheckout && (
              <>
                <div className='text-text-secondary text-center text-xs'>{t('payment.securePayment')}</div>
                <Button
                  onClick={handleCharge}
                  loading={isCharging}
                  disabled={!isReady || isCharging || !allFieldsValid}
                  className='w-full'
                  size='lg'
                >
                  {t('payment.payNow')} - {hostedFieldsData.sum} ₪
                </Button>
              </>
            )}

            {showBitCheckout && (
              <>
                <p className='text-text-secondary text-center text-sm'>{t('payment.bitCheckoutHint')}</p>
                <div className='text-text-secondary text-center text-xs'>{t('payment.securePayment')}</div>
                <Button
                  type='button'
                  onClick={handleChargeBit}
                  loading={isCharging}
                  disabled={!isReady || isCharging || !bitEligible}
                  className='w-full'
                  size='lg'
                >
                  {t('payment.payWithBit')} — {hostedFieldsData.sum} ₪
                </Button>
                {!bitEligible && <p className='text-text-secondary text-center text-xs'>{t('payment.bitMinimumAmount')}</p>}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
